import { Pencil } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type CollectionStandard,
  createStandardVintage,
  listStandards,
  type StandardRow,
} from "@/features/reference/api";
import { ApiError } from "@/lib/api";
import { isMoney } from "@/features/reference/money";

const ITEMS = [
  { key: "food", label: "Food" },
  { key: "housekeeping", label: "Housekeeping supplies" },
  { key: "apparel", label: "Apparel & services" },
  { key: "personal_care", label: "Personal care" },
  { key: "misc", label: "Miscellaneous" },
];
const SIZES = [1, 2, 3, 4];

// value key: `${item}__${size|add}`
function cellKey(item: string, size: number | "add") {
  return `${item}__${size}`;
}

export function NationalExpenseTab({
  effectiveOn,
  reloadToken,
}: {
  effectiveOn: string;
  reloadToken: number;
}) {
  const [rows, setRows] = useState<CollectionStandard[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const refetch = useCallback(() => {
    setLoading(true);
    listStandards({ standardType: "NATIONAL_EXPENSE", effectiveOn })
      .then((r) => setRows(r.items))
      .catch(() => toast.error("Couldn't load national expenses."))
      .finally(() => setLoading(false));
  }, [effectiveOn]);

  // reloadToken is a deliberate extra trigger (e.g. after a JSON import).
  useEffect(() => {
    refetch();
  }, [refetch, reloadToken]);

  function amount(item: string, size: number | null): string {
    return rows.find((r) => r.item === item && r.householdSize === size)?.monthlyAmount ?? "—";
  }

  if (loading) return <p className="text-muted-foreground text-sm">Loading…</p>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          Monthly national standard by household size (effective on {effectiveOn}).
        </p>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditOpen(true)}>
          <Pencil className="size-3.5" /> New vintage
        </Button>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              {SIZES.map((s) => (
                <TableHead key={s} className="text-right">
                  {s} {s === 1 ? "person" : "people"}
                </TableHead>
              ))}
              <TableHead className="text-right">Each add&apos;l</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ITEMS.map((it) => (
              <TableRow key={it.key}>
                <TableCell className="font-medium">{it.label}</TableCell>
                {SIZES.map((s) => (
                  <TableCell key={s} className="text-right tabular-nums">
                    {amount(it.key, s)}
                  </TableCell>
                ))}
                <TableCell className="text-right tabular-nums">{amount(it.key, null)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editOpen && (
        <EditVintageDialog
          current={rows}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function EditVintageDialog({
  current,
  onClose,
  onSaved,
}: {
  current: CollectionStandard[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = {};
    for (const it of ITEMS) {
      for (const s of SIZES) {
        const found = current.find((r) => r.item === it.key && r.householdSize === s);
        v[cellKey(it.key, s)] = found?.monthlyAmount ?? "";
      }
      const add = current.find((r) => r.item === it.key && r.householdSize === null);
      v[cellKey(it.key, "add")] = add?.monthlyAmount ?? "";
    }
    return v;
  });
  const [busy, setBusy] = useState(false);

  function setCell(key: string, val: string) {
    setValues((v) => ({ ...v, [key]: val }));
  }

  async function save() {
    const rows: StandardRow[] = [];
    for (const it of ITEMS) {
      for (const s of SIZES) {
        const val = (values[cellKey(it.key, s)] ?? "").trim();
        if (!isMoney(val)) {
          toast.error(`${it.label} — ${s} person: enter a valid amount.`);
          return;
        }
        rows.push({ standardType: "NATIONAL_EXPENSE", item: it.key, householdSize: s, monthlyAmount: val });
      }
      const addVal = (values[cellKey(it.key, "add")] ?? "").trim();
      if (!isMoney(addVal)) {
        toast.error(`${it.label} — each additional: enter a valid amount.`);
        return;
      }
      rows.push({ standardType: "NATIONAL_EXPENSE", item: it.key, householdSize: null, monthlyAmount: addVal });
    }
    setBusy(true);
    try {
      await createStandardVintage(effectiveDate, rows);
      toast.success("New national-expense vintage saved");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save the vintage.");
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>New national-expense vintage</DialogTitle>
          <DialogDescription>
            Saves a full grid at a new effective date. Existing vintages are left untouched.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 sm:max-w-xs">
          <Label htmlFor="ne-effective">Effective date</Label>
          <Input
            id="ne-effective"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
          />
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                {SIZES.map((s) => (
                  <TableHead key={s}>{s}p</TableHead>
                ))}
                <TableHead>Each add&apos;l</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ITEMS.map((it) => (
                <TableRow key={it.key}>
                  <TableCell className="font-medium">{it.label}</TableCell>
                  {SIZES.map((s) => (
                    <TableCell key={s}>
                      <Input
                        aria-label={`${it.label} ${s} person`}
                        value={values[cellKey(it.key, s)]}
                        onChange={(e) => setCell(cellKey(it.key, s), e.target.value)}
                        className="h-8 w-20"
                        inputMode="decimal"
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    <Input
                      aria-label={`${it.label} each additional`}
                      value={values[cellKey(it.key, "add")]}
                      onChange={(e) => setCell(cellKey(it.key, "add"), e.target.value)}
                      className="h-8 w-20"
                      inputMode="decimal"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save vintage"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
